import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import ToastMe from "@/components/ui/ToastMe";
import SystemService from "@/services/SystemService";
import { setBearerToken, setBranchId, setCompanyId } from "@/security/Axios";
import { authLogin } from "@/store/actions";

const BRAND = {
  background: "#f4f6fb",
  primary: "#156372",
  accent: "#FFC107",
};

const initialForm = {
  companyName: "",
  companyEmail: "",
  companyPhone: "",
  address: "",
  adminName: "",
  adminEmail: "",
  adminPhone: "",
  password: "",
  confirmPassword: "",
};

const SetupPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await SystemService.fetchOnboardingStatus();
      const data = response?.data || response;
      setStatus(data);

      if (!data?.licenseActive) {
        ToastMe("Please activate or renew a valid license first.", "warning");
        navigate("/license-status", { replace: true });
        return;
      }

      if (data?.setupComplete) {
        ToastMe("Workspace setup is already complete. Please sign in.", "info");
        navigate("/sign-in", { replace: true });
      }
    } catch (error) {
      ToastMe(error?.response?.data?.message || error?.message || "Unable to load setup status.", "error");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (form.password !== form.confirmPassword) {
      ToastMe("Passwords do not match.", "error");
      return;
    }

    setSaving(true);
    try {
      const response = await SystemService.completeOnboarding(form);
      const data = response?.data || {};
      const token = data?.token;
      const companyId = data?.company?.id;
      const branchId = data?.branch?.id;

      if (!token || !companyId) {
        ToastMe("Setup completed, but login token was not returned. Please sign in.", "warning");
        navigate("/sign-in", { replace: true });
        return;
      }

      setBearerToken(token);
      setCompanyId(companyId);
      if (branchId) setBranchId(branchId, data?.branch?.name);

      dispatch(
        authLogin({
          data: {
            token,
            data: {
              ...data.admin,
              company_id: companyId,
              default_branch_id: branchId,
            },
          },
          message: response?.message || "Workspace created successfully.",
          success: true,
        }),
      );

      ToastMe(response?.message || "Workspace created successfully.", "success");
      navigate("/admin/dashboard", { replace: true });
    } catch (error) {
      ToastMe(error?.response?.data?.message || error?.message || "Could not complete setup.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: BRAND.background }}>
        <div className="rounded-2xl bg-white border border-slate-200 px-6 py-5 text-slate-600 shadow-sm">
          Checking setup status...
        </div>
      </section>
    );
  }

  if (!status?.canOnboard) return null;

  return (
    <section className="min-h-screen px-4 py-10" style={{ backgroundColor: BRAND.background }}>
      <div className="mx-auto w-full max-w-3xl">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden">
          <div className="px-6 py-7 text-white" style={{ backgroundColor: BRAND.primary }}>
            <h1 className="text-2xl font-semibold text-white">Create Your Workspace</h1>
            <p className="mt-2 text-sm text-white/85">
              Your license is active. Create the company and first admin account for this Foods system.
            </p>
          </div>

          <form className="px-6 py-7 space-y-7" onSubmit={submit}>
            <div>
              <h2 className="text-base font-semibold text-slate-800">Company</h2>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <Field label="Company Name" name="companyName" value={form.companyName} onChange={updateField} required />
                <Field label="Company Email" name="companyEmail" type="email" value={form.companyEmail} onChange={updateField} />
                <Field label="Company Phone" name="companyPhone" value={form.companyPhone} onChange={updateField} />
                <Field label="Address" name="address" value={form.address} onChange={updateField} />
              </div>
            </div>

            <div>
              <h2 className="text-base font-semibold text-slate-800">First Admin</h2>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <Field label="Full Name" name="adminName" value={form.adminName} onChange={updateField} required />
                <Field label="Email" name="adminEmail" type="email" value={form.adminEmail} onChange={updateField} required />
                <Field label="Phone" name="adminPhone" value={form.adminPhone} onChange={updateField} required />
                <Field label="Password" name="password" type="password" value={form.password} onChange={updateField} required />
                <Field label="Confirm Password" name="confirmPassword" type="password" value={form.confirmPassword} onChange={updateField} required />
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <Link className="text-sm font-medium hover:underline" style={{ color: BRAND.primary }} to="/sign-in">
                Back to login
              </Link>
              <button
                type="submit"
                disabled={saving}
                className="rounded-xl px-6 py-3 font-semibold text-white transition disabled:opacity-70"
                style={{ backgroundColor: BRAND.accent, color: "#1f2937" }}
              >
                {saving ? "Creating..." : "Create Workspace"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, name, value, onChange, type = "text", required = false }) => (
  <label className="block">
    <span className="text-sm font-medium text-slate-700">{label}</span>
    <input
      className="mt-2 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-[#156372] focus:ring-2 focus:ring-[#156372]/15"
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      autoComplete={type === "password" ? "new-password" : "off"}
    />
  </label>
);

export default SetupPage;
